import { z } from "zod";

export const RoleSchema = z.enum([
  "admin",
  "manager",
  "sales",
  "support",
  "client",
  "lead",
]);

export type Role = z.infer<typeof RoleSchema>;

export const PipelineStageSchema = z.enum([
  "lead",
  "contact",
  "proposal",
  "negotiation",
  "client",
  "deployment",
  "post_sale",
]);

export type PipelineStage = z.infer<typeof PipelineStageSchema>;

export const DeploymentStageSchema = z.enum([
  "document_upload",
  "scheduling",
  "deployment",
  "evaluation",
]);

export type DeploymentStage = z.infer<typeof DeploymentStageSchema>;

export const LeadSourceSchema = z.enum([
  "landing_page",
  "calculator",
  "comparison",
  "demo_request",
  "whatsapp",
  "referral",
  "organic",
  "paid_ads",
  "event",
  "other",
]);

export type LeadSource = z.infer<typeof LeadSourceSchema>;

export const TicketStatusSchema = z.enum([
  "open",
  "in_progress",
  "waiting_client",
  "waiting_support",
  "resolved",
  "closed",
]);

export type TicketStatus = z.infer<typeof TicketStatusSchema>;

export const TicketPrioritySchema = z.enum([
  "low",
  "medium",
  "high",
  "urgent",
]);

export type TicketPriority = z.infer<typeof TicketPrioritySchema>;

export const LicenseStatusSchema = z.enum([
  "active",
  "expired",
  "suspended",
  "pending",
]);

export type LicenseStatus = z.infer<typeof LicenseStatusSchema>;

export const TrainingStatusSchema = z.enum([
  "scheduled",
  "in_progress",
  "completed",
  "cancelled",
]);

export type TrainingStatus = z.infer<typeof TrainingStatusSchema>;

export const DeploymentStatusSchema = z.enum([
  "pending",
  "documents_pending",
  "scheduled",
  "in_progress",
  "completed",
  "evaluation",
]);

export type DeploymentStatus = z.infer<typeof DeploymentStatusSchema>;

export const LeadStatusSchema = z.enum([
  "new",
  "contacted",
  "qualified",
  "proposal_sent",
  "negotiating",
  "won",
  "lost",
]);

export type LeadStatus = z.infer<typeof LeadStatusSchema>;

export const DemoStatusSchema = z.enum([
  "requested",
  "scheduled",
  "completed",
  "cancelled",
  "no_show",
]);

export type DemoStatus = z.infer<typeof DemoStatusSchema>;

export const ScheduleStatusSchema = z.enum([
  "scheduled",
  "confirmed",
  "completed",
  "cancelled",
  "rescheduled",
]);

export type ScheduleStatus = z.infer<typeof ScheduleStatusSchema>;

export const WhatsAppTemplateSchema = z.enum([
  "welcome",
  "demo_confirmation",
  "demo_reminder",
  "proposal_sent",
  "contract_sent",
  "document_request",
  "scheduling_confirmation",
  "deployment_start",
  "deployment_complete",
  "evaluation_request",
  "support_ticket_created",
  "support_ticket_update",
]);

export type WhatsAppTemplate = z.infer<typeof WhatsAppTemplateSchema>;

export const FAQCategorySchema = z.enum([
  "geral",
  "precos",
  "implantacao",
  "suporte",
  "licencas",
  "integracao",
  "seguranca",
]);

export type FAQCategory = z.infer<typeof FAQCategorySchema>;

export const BlogCategorySchema = z.enum([
  "novidades",
  "cases",
  "dicas",
  "tecnologia",
  "mercado",
  "tutoriais",
]);

export type BlogCategory = z.infer<typeof BlogCategorySchema>;

export const CaseStudyCategorySchema = z.enum([
  "varejo",
  "industria",
  "servicos",
  "ecommerce",
  "franquias",
]);

export type CaseStudyCategory = z.infer<typeof CaseStudyCategorySchema>;

export const TutorialCategorySchema = z.enum([
  "iniciando",
  "modulos",
  "integracoes",
  "relatorios",
  "admin",
  "avancado",
]);

export type TutorialCategory = z.infer<typeof TutorialCategorySchema>;

export const DownloadCategorySchema = z.enum([
  "contratos",
  "manuais",
  "apresentacoes",
  "cases",
  "checklists",
  "modelos",
]);

export type DownloadCategory = z.infer<typeof DownloadCategorySchema>;

export const PlanSchema = z.enum(["starter", "professional", "enterprise"]);

export type Plan = z.infer<typeof PlanSchema>;

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1).max(255),
  role: RoleSchema,
  avatarUrl: z.string().url().nullable().optional(),
  phone: z.string().nullable().optional(),
  whatsapp: z.string().nullable().optional(),
  companyId: z.string().uuid().nullable().optional(),
  isActive: z.boolean().default(true),
  emailVerified: z.boolean().default(false),
  lastLoginAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type User = z.infer<typeof UserSchema>;

export const CompanySchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  tradeName: z.string().max(255).nullable().optional(),
  cnpj: z.string().length(14).nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  whatsapp: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().length(2).nullable().optional(),
  zipCode: z.string().length(8).nullable().optional(),
  website: z.string().url().nullable().optional(),
  segment: z.string().nullable().optional(),
  size: z.enum(["micro", "small", "medium", "large"]).nullable().optional(),
  plan: PlanSchema.nullable().optional(),
  licenseStatus: LicenseStatusSchema.default("pending"),
  licenseExpiresAt: z.string().datetime().nullable().optional(),
  deploymentStatus: DeploymentStatusSchema.default("pending"),
  deploymentStartedAt: z.string().datetime().nullable().optional(),
  deploymentCompletedAt: z.string().datetime().nullable().optional(),
  assignedSalesId: z.string().uuid().nullable().optional(),
  assignedSupportId: z.string().uuid().nullable().optional(),
  notes: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Company = z.infer<typeof CompanySchema>;

export const LeadSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  whatsapp: z.string().nullable().optional(),
  company: z.string().max(255).nullable().optional(),
  role: z.string().max(100).nullable().optional(),
  source: LeadSourceSchema,
  status: LeadStatusSchema.default("new"),
  assignedTo: z.string().uuid().nullable().optional(),
  estimatedValue: z.number().nonnegative().nullable().optional(),
  notes: z.string().nullable().optional(),
  tags: z.array(z.string()).default([]),
  utmSource: z.string().nullable().optional(),
  utmMedium: z.string().nullable().optional(),
  utmCampaign: z.string().nullable().optional(),
  utmContent: z.string().nullable().optional(),
  utmTerm: z.string().nullable().optional(),
  lastContactAt: z.string().datetime().nullable().optional(),
  nextFollowUpAt: z.string().datetime().nullable().optional(),
  convertedAt: z.string().datetime().nullable().optional(),
  lostReason: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Lead = z.infer<typeof LeadSchema>;

export const ContactSchema = z.object({
  id: z.string().uuid(),
  leadId: z.string().uuid().nullable().optional(),
  companyId: z.string().uuid().nullable().optional(),
  name: z.string().min(1).max(255),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  whatsapp: z.string().nullable().optional(),
  role: z.string().max(100).nullable().optional(),
  isPrimary: z.boolean().default(false),
  notes: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Contact = z.infer<typeof ContactSchema>;

export const ProposalSchema = z.object({
  id: z.string().uuid(),
  leadId: z.string().uuid(),
  companyId: z.string().uuid().nullable().optional(),
  title: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  plan: PlanSchema,
  monthlyValue: z.number().positive(),
  setupFee: z.number().nonnegative().default(0),
  discount: z.number().nonnegative().max(100).default(0),
  validUntil: z.string().datetime(),
  status: z.enum(["draft", "sent", "viewed", "accepted", "rejected", "expired"]),
  sentAt: z.string().datetime().nullable().optional(),
  viewedAt: z.string().datetime().nullable().optional(),
  respondedAt: z.string().datetime().nullable().optional(),
  signedUrl: z.string().url().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdBy: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Proposal = z.infer<typeof ProposalSchema>;

export const NegotiationSchema = z.object({
  id: z.string().uuid(),
  proposalId: z.string().uuid(),
  stage: z.enum(["initial", "counter_proposal", "negotiating", "approved", "rejected"]),
  currentValue: z.number().positive(),
  targetValue: z.number().positive().nullable().optional(),
  notes: z.string().nullable().optional(),
  conductedBy: z.string().uuid(),
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Negotiation = z.infer<typeof NegotiationSchema>;

export const ContractSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  proposalId: z.string().uuid().nullable().optional(),
  contractNumber: z.string().min(1).max(50),
  plan: PlanSchema,
  monthlyValue: z.number().positive(),
  setupFee: z.number().nonnegative().default(0),
  startDate: z.string().date(),
  endDate: z.string().date().nullable().optional(),
  autoRenew: z.boolean().default(true),
  status: z.enum(["draft", "active", "expired", "cancelled", "suspended"]),
  signedUrl: z.string().url().nullable().optional(),
  signedAt: z.string().datetime().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Contract = z.infer<typeof ContractSchema>;

export const LicenseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  contractId: z.string().uuid().nullable().optional(),
  plan: PlanSchema,
  status: LicenseStatusSchema.default("pending"),
  maxUsers: z.number().positive(),
  currentUsers: z.number().nonnegative().default(0),
  features: z.array(z.string()).default([]),
  startsAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable().optional(),
  autoRenew: z.boolean().default(true),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type License = z.infer<typeof LicenseSchema>;

export const DeploymentSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  contractId: z.string().uuid().nullable().optional(),
  status: DeploymentStatusSchema.default("pending"),
  currentStage: DeploymentStageSchema.default("document_upload"),
  assignedTo: z.string().uuid().nullable().optional(),
  scheduledAt: z.string().datetime().nullable().optional(),
  startedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  evaluatedAt: z.string().datetime().nullable().optional(),
  evaluationScore: z.number().min(1).max(5).nullable().optional(),
  evaluationNotes: z.string().nullable().optional(),
  documents: z.array(z.string()).default([]),
  notes: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Deployment = z.infer<typeof DeploymentSchema>;

export const DocumentSchema = z.object({
  id: z.string().uuid(),
  deploymentId: z.string().uuid().nullable().optional(),
  companyId: z.string().uuid().nullable().optional(),
  leadId: z.string().uuid().nullable().optional(),
  name: z.string().min(1).max(255),
  type: z.enum(["contract", "document", "manual", "presentation", "checklist", "other"]),
  category: z.string().max(100).nullable().optional(),
  url: z.string().url(),
  size: z.number().positive(),
  mimeType: z.string().max(100),
  uploadedBy: z.string().uuid(),
  isPublic: z.boolean().default(false),
  expiresAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Document = z.infer<typeof DocumentSchema>;

export const TicketSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid().nullable().optional(),
  leadId: z.string().uuid().nullable().optional(),
  contactId: z.string().uuid().nullable().optional(),
  title: z.string().min(1).max(255),
  description: z.string().min(1),
  status: TicketStatusSchema.default("open"),
  priority: TicketPrioritySchema.default("medium"),
  category: z.string().max(100).nullable().optional(),
  assignedTo: z.string().uuid().nullable().optional(),
  tags: z.array(z.string()).default([]),
  dueAt: z.string().datetime().nullable().optional(),
  resolvedAt: z.string().datetime().nullable().optional(),
  closedAt: z.string().datetime().nullable().optional(),
  satisfactionScore: z.number().min(1).max(5).nullable().optional(),
  satisfactionComment: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Ticket = z.infer<typeof TicketSchema>;

export const TicketMessageSchema = z.object({
  id: z.string().uuid(),
  ticketId: z.string().uuid(),
  authorId: z.string().uuid(),
  content: z.string().min(1),
  isInternal: z.boolean().default(false),
  attachments: z.array(z.string()).default([]),
  createdAt: z.string().datetime(),
});

export type TicketMessage = z.infer<typeof TicketMessageSchema>;

export const TrainingSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid().nullable().optional(),
  leadId: z.string().uuid().nullable().optional(),
  title: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  type: z.enum(["online", "presencial", "video", "document"]),
  category: TutorialCategorySchema.nullable().optional(),
  status: TrainingStatusSchema.default("scheduled"),
  instructorId: z.string().uuid().nullable().optional(),
  scheduledAt: z.string().datetime().nullable().optional(),
  startedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  duration: z.number().nonnegative().nullable().optional(),
  attendees: z.array(z.string().uuid()).default([]),
  materials: z.array(z.string()).default([]),
  recordingUrl: z.string().url().nullable().optional(),
  feedbackScore: z.number().min(1).max(5).nullable().optional(),
  feedbackComment: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Training = z.infer<typeof TrainingSchema>;

export const ScheduleSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  type: z.enum(["meeting", "demo", "training", "deployment", "call", "other"]),
  status: ScheduleStatusSchema.default("scheduled"),
  startAt: z.string().datetime(),
  endAt: z.string().datetime(),
  location: z.string().max(255).nullable().optional(),
  meetingUrl: z.string().url().nullable().optional(),
  leadId: z.string().uuid().nullable().optional(),
  companyId: z.string().uuid().nullable().optional(),
  assignedTo: z.string().uuid().nullable().optional(),
  attendees: z.array(z.string().uuid()).default([]),
  reminderSent: z.boolean().default(false),
  reminderAt: z.string().datetime().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Schedule = z.infer<typeof ScheduleSchema>;

export const DemoRequestSchema = z.object({
  id: z.string().uuid(),
  leadId: z.string().uuid().nullable().optional(),
  name: z.string().min(1).max(255),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  whatsapp: z.string().nullable().optional(),
  company: z.string().max(255).nullable().optional(),
  role: z.string().max(100).nullable().optional(),
  preferredDate: z.string().date().nullable().optional(),
  preferredTime: z.string().time().nullable().optional(),
  message: z.string().nullable().optional(),
  status: DemoStatusSchema.default("requested"),
  scheduledAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
  assignedTo: z.string().uuid().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type DemoRequest = z.infer<typeof DemoRequestSchema>;

export const ComparisonRequestSchema = z.object({
  id: z.string().uuid(),
  leadId: z.string().uuid().nullable().optional(),
  name: z.string().min(1).max(255),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  whatsapp: z.string().nullable().optional(),
  company: z.string().max(255).nullable().optional(),
  currentERP: z.string().max(255).nullable().optional(),
  companySize: z.enum(["micro", "small", "medium", "large"]).nullable().optional(),
  segment: z.string().max(100).nullable().optional(),
  requirements: z.array(z.string()).default([]),
  createdAt: z.string().datetime(),
});

export type ComparisonRequest = z.infer<typeof ComparisonRequestSchema>;

export const WhatsAppMessageSchema = z.object({
  id: z.string().uuid(),
  leadId: z.string().uuid().nullable().optional(),
  companyId: z.string().uuid().nullable().optional(),
  contactId: z.string().uuid().nullable().optional(),
  to: z.string().min(10).max(20),
  from: z.string().min(10).max(20),
  template: WhatsAppTemplateSchema.nullable().optional(),
  message: z.string().min(1),
  status: z.enum(["pending", "sent", "delivered", "read", "failed"]),
  messageId: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
  sentAt: z.string().datetime().nullable().optional(),
  deliveredAt: z.string().datetime().nullable().optional(),
  readAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
});

export type WhatsAppMessage = z.infer<typeof WhatsAppMessageSchema>;

export const BlogPostSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(255),
  slug: z.string().min(1).max(255),
  excerpt: z.string().max(500).nullable().optional(),
  content: z.string().min(1),
  coverImage: z.string().url().nullable().optional(),
  category: BlogCategorySchema,
  tags: z.array(z.string()).default([]),
  authorId: z.string().uuid(),
  status: z.enum(["draft", "published", "archived"]).default("draft"),
  publishedAt: z.string().datetime().nullable().optional(),
  seoTitle: z.string().max(60).nullable().optional(),
  seoDescription: z.string().max(160).nullable().optional(),
  seoKeywords: z.array(z.string()).default([]),
  views: z.number().nonnegative().default(0),
  readTime: z.number().positive().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type BlogPost = z.infer<typeof BlogPostSchema>;

export const FAQSchema = z.object({
  id: z.string().uuid(),
  question: z.string().min(1).max(500),
  answer: z.string().min(1),
  category: FAQCategorySchema,
  tags: z.array(z.string()).default([]),
  order: z.number().int().nonnegative().default(0),
  isPublished: z.boolean().default(true),
  viewCount: z.number().nonnegative().default(0),
  helpfulCount: z.number().nonnegative().default(0),
  notHelpfulCount: z.number().nonnegative().default(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type FAQ = z.infer<typeof FAQSchema>;

export const TutorialSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(255),
  slug: z.string().min(1).max(255),
  description: z.string().max(500).nullable().optional(),
  content: z.string().min(1),
  coverImage: z.string().url().nullable().optional(),
  category: TutorialCategorySchema,
  tags: z.array(z.string()).default([]),
  authorId: z.string().uuid(),
  difficulty: z.enum(["beginner", "intermediate", "advanced"]).default("beginner"),
  estimatedTime: z.number().positive().nullable().optional(),
  videoUrl: z.string().url().nullable().optional(),
  isPublished: z.boolean().default(true),
  viewCount: z.number().nonnegative().default(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Tutorial = z.infer<typeof TutorialSchema>;

export const DownloadSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(255),
  description: z.string().max(500).nullable().optional(),
  fileUrl: z.string().url(),
  fileName: z.string().min(1).max(255),
  fileSize: z.number().positive(),
  mimeType: z.string().max(100),
  category: DownloadCategorySchema,
  tags: z.array(z.string()).default([]),
  isPublic: z.boolean().default(true),
  downloadCount: z.number().nonnegative().default(0),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Download = z.infer<typeof DownloadSchema>;

export const CaseStudySchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(255),
  slug: z.string().min(1).max(255),
  clientName: z.string().min(1).max(255),
  clientSegment: CaseStudyCategorySchema,
  clientSize: z.enum(["micro", "small", "medium", "large"]),
  challenge: z.string().min(1),
  solution: z.string().min(1),
  results: z.string().min(1),
  testimonial: z.string().nullable().optional(),
  coverImage: z.string().url().nullable().optional(),
  images: z.array(z.string().url()).default([]),
  metrics: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
  isPublished: z.boolean().default(true),
  publishedAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type CaseStudy = z.infer<typeof CaseStudySchema>;

export const SEOSettingSchema = z.object({
  id: z.string().uuid(),
  path: z.string().min(1).max(255),
  title: z.string().max(60).nullable().optional(),
  description: z.string().max(160).nullable().optional(),
  keywords: z.array(z.string()).default([]),
  ogTitle: z.string().max(60).nullable().optional(),
  ogDescription: z.string().max(160).nullable().optional(),
  ogImage: z.string().url().nullable().optional(),
  twitterTitle: z.string().max(60).nullable().optional(),
  twitterDescription: z.string().max(160).nullable().optional(),
  twitterImage: z.string().url().nullable().optional(),
  canonicalUrl: z.string().url().nullable().optional(),
  robots: z.string().max(100).nullable().optional(),
  structuredData: z.record(z.string(), z.unknown()).nullable().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type SEOSetting = z.infer<typeof SEOSettingSchema>;

export const AnalyticsEventSchema = z.object({
  id: z.string().uuid(),
  event: z.string().min(1).max(100),
  properties: z.record(z.string(), z.unknown()).default({}),
  userId: z.string().uuid().nullable().optional(),
  sessionId: z.string().nullable().optional(),
  page: z.string().max(255).nullable().optional(),
  referrer: z.string().max(500).nullable().optional(),
  userAgent: z.string().max(500).nullable().optional(),
  ip: z.string().max(45).nullable().optional(),
  country: z.string().length(2).nullable().optional(),
  createdAt: z.string().datetime(),
});

export type AnalyticsEvent = z.infer<typeof AnalyticsEventSchema>;

export const APIResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.nullable().optional(),
    error: z.object({
      code: z.string(),
      message: z.string(),
      details: z.record(z.string(), z.unknown()).optional(),
    }).nullable().optional(),
    meta: z.object({
      page: z.number().int().positive().optional(),
      limit: z.number().int().positive().optional(),
      total: z.number().int().nonnegative().optional(),
      totalPages: z.number().int().nonnegative().optional(),
    }).optional(),
  });

export type APIResponse<_T> = z.infer<ReturnType<typeof APIResponseSchema<z.ZodTypeAny>>>;

export const PaginationSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  sortBy: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
});

export type Pagination = z.infer<typeof PaginationSchema>;

export const FilterSchema = z.object({
  search: z.string().optional(),
  status: z.string().optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  tags: z.array(z.string()).optional(),
});

export type Filter = z.infer<typeof FilterSchema>;
